/* ==========================================================================
   TheraMotion — product.js
   Media gallery, zoom lightbox, mobile sticky add-to-cart, purchase options.
   ========================================================================== */
(() => {
  const DL = window.DL;
  if (!DL) return;

  /* ---------- Gallery ---------- */
  class DLGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.slides = DL.qsa('.dl-gallery__slide', this.track);
      this.dots = DL.qsa('[data-gallery-dots] button', this);
      this.thumbs = DL.qsa('.dl-gallery__thumb', this);
      this.counter = this.querySelector('[data-gallery-current]');
      this.lightbox = this.querySelector('[data-lightbox]');
      this.index = 0;

      this.addEventListener('click', (e) => {
        const go = e.target.closest('[data-go]');
        if (go) return this.goTo(parseInt(go.dataset.go, 10));
        if (e.target.closest('[data-gallery-prev]')) return this.goTo(this.index - 1);
        if (e.target.closest('[data-gallery-next]')) return this.goTo(this.index + 1);
        const zoom = e.target.closest('[data-zoom]');
        if (zoom && this.lightbox) this.lightbox.open(parseInt(zoom.dataset.zoom, 10), zoom);
      });

      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.goTo(this.index + 1);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.goTo(this.index - 1);
        }
      });

      if ('IntersectionObserver' in window && this.slides.length > 1) {
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) this.setActive(parseInt(entry.target.dataset.index, 10));
            });
          },
          { root: this.track, threshold: [0.6] }
        );
        this.slides.forEach((slide) => this.io.observe(slide));
      }

      this.onVariant = (e) => {
        if (String(e.detail.productId) !== this.dataset.productId || !e.detail.variant) return;
        const mediaId = e.detail.variant.featured_media_id;
        if (mediaId) this.goToMedia(String(mediaId));
      };
      DL.on('variant:change', this.onVariant);

      const initial = this.dataset.initialMedia;
      if (initial) {
        const slide = this.slides.find((s) => s.dataset.mediaId === initial);
        if (slide && parseInt(slide.dataset.index, 10) > 0) {
          window.requestAnimationFrame(() => this.goTo(parseInt(slide.dataset.index, 10), 'auto'));
        }
      }
    }
    disconnectedCallback() {
      document.removeEventListener('dl:variant:change', this.onVariant);
      this.io && this.io.disconnect();
    }
    goToMedia(mediaId) {
      const slide = this.slides.find((s) => s.dataset.mediaId === mediaId);
      if (slide) this.goTo(parseInt(slide.dataset.index, 10));
    }
    goTo(i, behavior = 'smooth') {
      if (!this.slides.length) return;
      const count = this.slides.length;
      const index = ((i % count) + count) % count;
      const slide = this.slides[index];
      this.track.scrollTo({ left: slide.offsetLeft, behavior: DL.reduceMotion ? 'auto' : behavior });
      this.setActive(index);
    }
    setActive(index) {
      if (index === this.index && this._initialised) return;
      this._initialised = true;
      this.index = index;
      this.dots.forEach((d, i) => d.setAttribute('aria-current', String(i === index)));
      this.thumbs.forEach((t, i) => {
        const active = i === index;
        t.setAttribute('aria-current', String(active));
        if (active && t.parentElement && t.parentElement.parentElement) {
          const list = t.parentElement.parentElement;
          const left = t.parentElement.offsetLeft - list.clientWidth / 2 + t.clientWidth / 2;
          list.scrollTo({ left, behavior: DL.reduceMotion ? 'auto' : 'smooth' });
        }
      });
      if (this.counter) this.counter.textContent = String(index + 1);
      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (i === index) {
          if (!DL.reduceMotion) video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }
  }
  if (!customElements.get('dl-gallery')) customElements.define('dl-gallery', DLGallery);

  /* ---------- Lightbox ---------- */
  class DLLightbox extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-lightbox-track]');
      this.slides = DL.qsa('.dl-lightbox__slide', this);
      this.count = this.querySelector('[data-lightbox-count]');
      this.current = 0;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-lightbox-close]')) return this.close();
        if (e.target.closest('[data-lightbox-prev]')) return this.show(this.current - 1);
        if (e.target.closest('[data-lightbox-next]')) return this.show(this.current + 1);
        const fig = e.target.closest('.dl-lightbox__slide');
        if (fig && e.target.tagName === 'IMG') this.toggleZoom(fig, e);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowRight') this.show(this.current + 1);
        if (e.key === 'ArrowLeft') this.show(this.current - 1);
        if (e.key === 'Tab') {
          const items = DL.focusable(this);
          if (!items.length) return;
          if (e.shiftKey && document.activeElement === items[0]) {
            e.preventDefault();
            items[items.length - 1].focus();
          } else if (!e.shiftKey && document.activeElement === items[items.length - 1]) {
            e.preventDefault();
            items[0].focus();
          }
        }
      });
      this.addEventListener('mousemove', (e) => {
        const fig = e.target.closest('.dl-lightbox__slide.is-zoomed');
        if (fig) this.pan(fig, e);
      });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => entries.forEach((en) => en.isIntersecting && en.intersectionRatio > 0.6 && this.setCurrent(this.slides.indexOf(en.target))),
          { root: this.track, threshold: [0.6] }
        );
        this.slides.forEach((s) => this.io.observe(s));
      }
    }
    open(galleryIndex, opener) {
      this.opener = opener;
      let target = this.slides.findIndex((s) => parseInt(s.dataset.index, 10) === galleryIndex);
      if (target < 0) target = 0;
      this.slides.forEach((s) => {
        const img = s.querySelector('img[data-src]');
        if (img && !img.src) img.src = img.dataset.src;
      });
      this.hidden = false;
      document.documentElement.classList.add('dl-lock');
      window.requestAnimationFrame(() => {
        this.setAttribute('open', '');
        this.show(target, 'auto');
        const closeBtn = this.querySelector('[data-lightbox-close]');
        closeBtn && closeBtn.focus({ preventScroll: true });
      });
    }
    close() {
      this.removeAttribute('open');
      this.slides.forEach((s) => s.classList.remove('is-zoomed'));
      if (!DL.qs('dl-drawer[open]')) document.documentElement.classList.remove('dl-lock');
      window.setTimeout(() => (this.hidden = true), DL.reduceMotion ? 0 : 400);
      this.opener && this.opener.focus({ preventScroll: true });
    }
    show(i, behavior = 'smooth') {
      if (!this.slides.length) return;
      const n = this.slides.length;
      const index = ((i % n) + n) % n;
      this.slides.forEach((s) => s.classList.remove('is-zoomed'));
      this.track.scrollTo({ left: this.slides[index].offsetLeft, behavior: DL.reduceMotion ? 'auto' : behavior });
      this.setCurrent(index);
    }
    setCurrent(index) {
      if (index < 0) return;
      this.current = index;
      if (this.count) this.count.textContent = `${index + 1} / ${this.slides.length}`;
    }
    toggleZoom(fig, e) {
      const zoomed = fig.classList.toggle('is-zoomed');
      if (zoomed) this.pan(fig, e);
    }
    pan(fig, e) {
      const img = fig.querySelector('img');
      if (!img) return;
      const rect = img.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = `${Math.max(0, Math.min(100, x))}% ${Math.max(0, Math.min(100, y))}%`;
    }
  }
  if (!customElements.get('dl-lightbox')) customElements.define('dl-lightbox', DLLightbox);

  /* ---------- Mobile sticky add to cart ---------- */
  class DLStickyAtc extends HTMLElement {
    connectedCallback() {
      this.scope = this.closest('[data-product-scope]');
      this.mainBtn = this.scope && this.scope.querySelector('[data-main-atc]');
      this.btn = this.querySelector('button');
      this.img = this.querySelector('[data-sticky-img]');
      if (!this.mainBtn || !('IntersectionObserver' in window)) return;

      this.mq = window.matchMedia('(max-width: 989px)');
      this.pastMain = false;
      this.hideZones = new Set();
      this.consentVisible = false;
      this.visible = false;

      this.mainInView = false;
      this.mainObserver = new IntersectionObserver(
        ([entry]) => {
          this.mainInView = entry.isIntersecting;
          this.checkPast();
        },
        { threshold: 0 }
      );
      this.mainObserver.observe(this.mainBtn);

      // Jumps (anchor links, restored scroll) can skip the button without an intersection change.
      let ticking = false;
      window.addEventListener(
        'scroll',
        () => {
          if (ticking || this.mainInView) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            this.checkPast();
          });
        },
        { passive: true }
      );

      this.zoneObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => (en.isIntersecting ? this.hideZones.add(en.target) : this.hideZones.delete(en.target)));
          this.update();
        },
        { threshold: 0 }
      );
      DL.qsa('[data-sticky-hide], .dl-footer-section').forEach((el) => this.zoneObserver.observe(el));

      const onMq = () => this.update();
      if (this.mq.addEventListener) this.mq.addEventListener('change', onMq);
      else this.mq.addListener(onMq);

      DL.on('variant:change', (e) => {
        if (String(e.detail.productId) !== this.dataset.productId || !e.detail.variant) return;
        if (this.img && e.detail.variant.image) this.img.src = e.detail.variant.image;
      });
      DL.on('drawer:open', () => this.update());
      DL.on('drawer:close', () => this.update());
      this.watchConsent();
    }
    checkPast() {
      const past = !this.mainInView && this.mainBtn.getBoundingClientRect().bottom < 0;
      if (past === this.pastMain) return;
      this.pastMain = past;
      this.update();
    }
    update() {
      const show =
        (this.mq.matches || this.dataset.desktop === 'true') && this.pastMain && this.hideZones.size === 0 && !this.consentVisible && !DL.qs('dl-drawer[open]');
      if (show === this.visible) return;
      this.visible = show;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      if (this.btn) this.btn.tabIndex = show ? 0 : -1;
      document.documentElement.classList.toggle('dl-sticky-visible', show);
    }
    watchConsent() {
      const selectors = ['#shopify-pc__banner', '#shopify-pc__prefs', '[data-cookie-banner]'];
      const check = () => {
        let visible = false;
        selectors.forEach((sel) => {
          const el = document.querySelector(sel);
          if (!el) return;
          const cs = window.getComputedStyle(el);
          const r = el.getBoundingClientRect();
          if (cs.display !== 'none' && cs.visibility !== 'hidden' && parseFloat(cs.opacity) > 0 && r.height > 0 && r.top < window.innerHeight && r.bottom > 0) {
            visible = true;
          }
        });
        if (visible !== this.consentVisible) {
          this.consentVisible = visible;
          this.update();
        }
      };
      check();
      const mo = new MutationObserver(DL.debounce(check, 150));
      mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] });
      document.addEventListener('visitorConsentCollected', () => window.setTimeout(check, 60));
    }
  }
  if (!customElements.get('dl-sticky-atc')) customElements.define('dl-sticky-atc', DLStickyAtc);

  /* ---------- Purchase options (selling plans) ---------- */
  class DLPurchaseOptions extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-plan-input]');
      this.addEventListener('change', (e) => {
        const radio = e.target.closest('input[type="radio"]');
        const select = e.target.closest('[data-plan-select]');
        if (select) {
          const label = select.closest('.dl-plan');
          const r = label && label.querySelector('input[type="radio"]');
          if (r) {
            r.value = select.value;
            r.checked = true;
          }
        }
        const checked = this.querySelector('input[type="radio"]:checked');
        DL.qsa('.dl-plan', this).forEach((p) => p.classList.toggle('is-selected', p.contains(checked)));
        if (this.input && checked) this.input.value = checked.value;
        if (radio || select) DL.emit('plan:change', { plan: checked ? checked.value : '' });
      });
    }
  }
  if (!customElements.get('dl-purchase-options')) customElements.define('dl-purchase-options', DLPurchaseOptions);
})();
